<template>
  <div class="top-header-wrap">
  <header class="top-header" :class="{ 'top-header--guest': !isLoggedIn }">
    <a href="#" class="logo" @click.prevent="$emit('home')">
      <span
        v-if="logoLoading"
        class="logo-skeleton"
        :aria-label="$t('common.loading')"
        role="status"
      />
      <img v-else-if="logoUrl" :src="logoUrl" :alt="brandName" class="logo-img">
      <span v-else class="logo-crown">♛</span>
    </a>

    <div class="top-header__center">
      <nav class="main-nav">
        <a
          v-for="(item, idx) in topNavItems"
          :key="item.key"
          href="#"
          :class="{ active: activeNav === idx }"
          @click.prevent="$emit('nav', idx)"
        >{{ item.label }}</a>
      </nav>

      <button
        v-if="isLoggedIn && slipUnreadCount > 0"
        type="button"
        class="header-slip-notice"
        :aria-label="slipNoticeAria"
        @click="$emit('open-slip')"
      >
        <span class="header-slip-notice__dot" aria-hidden="true" />
        <span class="header-slip-notice__text">{{ slipNoticeText }}</span>
      </button>
    </div>

    <div class="header-buttons">
      <div v-if="isLoggedIn" class="header-user">
        <strong class="header-user__id">{{ loginId }}</strong>
        <span class="header-user__sep" aria-hidden="true" />
        <strong class="header-user__balance">{{ displayBalance }}</strong>
        <button
          type="button"
          class="header-user__refresh"
          :class="{ 'header-user__refresh--loading': balanceLoading }"
          :aria-label="$t('common.balanceRefresh')"
          :disabled="balanceLoading"
          @click="$emit('refresh-balance')"
        >
          <svg
            class="header-user__refresh-icon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            aria-hidden="true"
          >
            <path
              d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5M4 12a8 8 0 1 1 2.34 5.66M4 20v-5h5"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button type="button" class="btn-logout" @click="$emit('logout')">{{ $t('nav.logout') }}</button>
      </div>

      <template v-else>
        <button type="button" class="btn-login" @click="$emit('login')">{{ $t('nav.login') }}</button>
        <button type="button" class="btn-join" @click="$emit('signup')">{{ $t('nav.signup') }}</button>
      </template>

      <button
        type="button"
        class="btn-lang"
        :aria-label="$t('lang.title')"
        @click="$emit('lang')"
      >
        <svg class="btn-lang__icon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.75" />
          <path
            d="M3 12h18M12 3c2.5 2.8 4 6 4 9s-1.5 6.2-4 9M12 3c-2.5 2.8-4 6-4 9s1.5 6.2 4 9"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
          />
        </svg>
        <span class="btn-lang__code">{{ localeShort }}</span>
      </button>
      <button
        type="button"
        class="btn-menu"
        :class="{ 'btn-menu--open': menuOpen }"
        :aria-label="menuOpen ? $t('nav.menuClose') : $t('nav.menuOpen')"
        @click="$emit('toggle-menu')"
      >{{ menuOpen ? '✕' : '☰' }}</button>
    </div>
  </header>

  <button
    v-if="isLoggedIn && slipUnreadCount > 0"
    type="button"
    class="header-slip-banner"
    :aria-label="slipNoticeAria"
    @click="$emit('open-slip')"
  >
    <span class="header-slip-banner__dot" aria-hidden="true" />
    <span class="header-slip-banner__text">{{ slipNoticeText }}</span>
  </button>
  </div>
</template>

<script>
import { getLocaleMeta } from '@/utils/locale'
import { resolveSiteLogoUrl } from '@/utils/siteBrand'

export default {
  name: 'BlackGoldHeader',
  props: {
    activeNav: { type: Number, default: 0 },
    isLoggedIn: { type: Boolean, default: false },
    loginId: { type: String, default: '' },
    balance: { type: Number, default: null },
    balanceLoading: { type: Boolean, default: false },
    menuOpen: { type: Boolean, default: false },
    slipUnreadCount: { type: Number, default: 0 },
    topNavItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    logoLoading () {
      return this.$store.state.siteInfo == null
    },
    logoUrl () {
      if (this.logoLoading) return ''
      return resolveSiteLogoUrl(this.$store.state.siteInfo)
    },
    brandName () {
      return this.$t('brand.name')
    },
    displayBalance () {
      if (this.balanceLoading && (this.balance === null || this.balance === undefined)) {
        return this.$t('common.loading')
      }
      return this.formatBalance(this.balance)
    },
    slipNoticeText () {
      return this.$t('slip.headerNotice')
    },
    slipNoticeAria () {
      return this.$t('slip.headerNoticeAria')
    },
    localeShort () {
      const locale = this.$i18n.locale
      return getLocaleMeta(locale).short || String(locale || 'ko').toUpperCase()
    }
  }
}
</script>
